<template>
  <MRow :gutter="0" class="flex-1">
    <MCol :size="12">
      <FlotoCrudContainer
        ref="list"
        :fetch-fn="getAuditItems"
        :scrollable="false"
        :resource-name="$t('audit_trail')"
      >
        <template v-slot:add-controls="{ refreshList, items }">
          <div
            v-if="!showSearchRow"
            class="flex justify-end items-center"
            :gutter="0"
          >
            <div class="flex-1 text-right">
              <span class="text-xs text-light mr-2">
                {{ startDate | datetime }} -
                {{ endDate | datetime }}
              </span>
            </div>
            <div class="text-right">
              <MTooltip>
                <template v-slot:trigger>
                  <MButton
                    shape="circle"
                    variant="neutral-lighter"
                    :shadow="false"
                    @click="toggleSearchRow"
                  >
                    <MIcon name="filter" size="sm" />
                  </MButton>
                </template>
                {{ $tc('filter') }}
              </MTooltip>
            </div>
            <div class="text-right">
              <ExportButton
                v-if="(items || []).length > 0"
                :button-text="$t('download')"
                class="mx-1"
                :header="$t('download')"
                :file-name="
                  $t('module_audit_trail', { module: $tc(moduleName) })
                "
                :options="[
                  { text: $t('pdf'), value: 'pdf' },
                  { text: $t('excel'), value: 'excel' },
                  { text: $t('csv'), value: 'csv' },
                ]"
                :default-data="{ exportFormat: 'pdf' }"
                :download-fn="handleDownload"
              >
                <template v-slot:trigger>
                  <MTooltip>
                    <template v-slot:trigger>
                      <MButton
                        shape="circle"
                        variant="neutral-lighter"
                        :shadow="false"
                      >
                        <MIcon name="download" size="sm" />
                      </MButton>
                    </template>
                    {{ $tc('download') }}
                  </MTooltip>
                </template>
              </ExportButton>
            </div>
          </div>
          <transition
            v-if="showSearchRow"
            name="slideBottom"
            :duration="{ leave: 0 }"
            mode="out-in"
          >
            <MAffix :offset-top="40">
              <MRow
                class="items-center bordered rounded p-2 bg-page relative mb-1"
                :gutter="0"
              >
                <MCol>
                  <div class="flex flex-1 items-center pr-8">
                    <FlotoForm
                      ref="formRef"
                      class="flex items-center"
                      @submit="refreshList"
                    >
                      <MCol auto-size class="pr-2">
                        <FlotoFormItem
                          id="from-picker"
                          :label="$t('from')"
                          :as-input="false"
                          :rules="{
                            // eslint-disable-next-line
                            max_date: endDate,
                          }"
                        >
                          <FlotoDatePicker
                            v-model="startDate"
                            :allow-clear="false"
                            :max-date="endDate"
                          />
                        </FlotoFormItem>
                      </MCol>
                      <MCol auto-size class="px-2">
                        <FlotoFormItem
                          id="to-picker"
                          :rules="{
                            // eslint-disable-next-line
                            min_date: startDate,
                          }"
                          :as-input="false"
                          :label="$t('to')"
                        >
                          <FlotoDatePicker
                            v-model="endDate"
                            :allow-clear="false"
                            :min-date="startDate"
                          />
                        </FlotoFormItem>
                      </MCol>

                      <template v-slot:submit>
                        <span />
                      </template>
                    </FlotoForm>

                    <MButton
                      id="search-btn"
                      class="mt-2 ml-3"
                      :loading="searchLoading"
                      @click="submitForm"
                    >
                      {{ $t('search') }}
                    </MButton>
                  </div>
                </MCol>
                <MCol
                  class="ml-auto flex justify-end items-start"
                  style="margin-top: -2.5% !important"
                >
                  <MButton
                    id="cancel-btn"
                    shape="circle"
                    variant="default"
                    :shadow="false"
                    class="button-neutral-lighter rounded"
                    @click="toggleSearchRow"
                  >
                    <MIcon name="times" />
                  </MButton>
                </MCol>
              </MRow>
            </MAffix>
          </transition>
        </template>
        <template v-slot:list-items="{ items }">
          <MRow>
            <MCol class="my-2">
              <MTimeline mode="left" class="pl-2">
                <Audit
                  v-for="timeline in items"
                  :key="timeline.id"
                  :parent-id="resourceId"
                  :module-name="moduleName"
                  v-bind="timeline"
                />
              </MTimeline>
            </MCol>
          </MRow>
        </template>
      </FlotoCrudContainer>
    </MCol>
  </MRow>
</template>

<script>
import { getAuditApi } from './api'
import Audit from './audit'
import Moment from 'moment'
import ExportButton from '@modules/reports/components/export-button'
import { getEncryptedPassword } from '@utils/password'
import { downloadApi } from '@src/modules/reports/reports-api'
import Pick from 'lodash/pick'
import { PreferenceComputed } from '@state/modules/preference'
export default {
  name: 'AuditContainer',
  components: { Audit, ExportButton },
  props: {
    resourceId: { type: Number, required: true },
    moduleName: { type: String, required: true },
  },
  data() {
    return {
      searchLoading: false,
      startDate: Moment().subtract(6, 'months').startOf('day').unix() * 1000,
      endDate: Moment().endOf('day').unix() * 1000,
      showSearchRow: false,
    }
  },
  computed: {
    ...Pick(PreferenceComputed, ['attachmentPassword']),
  },
  methods: {
    getAuditItems(limit, offset) {
      this.searchLoading = true
      return getAuditApi(
        {
          startDate: this.startDate,
          endDate: this.endDate,
          ...(this.applyComponentTypeFilter
            ? { componentType: this.componentType }
            : {}),
          ...(this.applyDiscoveryMethodFilter
            ? { discoveryMethod: this.discoveryMethod }
            : {}),
        },
        this.moduleName,
        this.resourceId,
        limit,
        offset
      ).finally(() => (this.searchLoading = false))
    },
    refresh() {
      this.$refs.list.refresh()
    },
    submitForm() {
      if (this.searchLoading) {
        return
      }
      this.$refs.formRef.submit()
    },
    getAvailableAttachmentPassword(data) {
      const availableAttachmentPassword = data.protectedPassword
        ? getEncryptedPassword(data.protectedPassword)
        : getEncryptedPassword(this.attachmentPassword)
      return data.enabled ? availableAttachmentPassword : ''
    },
    handleDownload(data) {
      const requestData = {}
      requestData.model = this.$constants.AUDIT
      requestData.exportFormat = data.exportFormat
      requestData.enabled = data.enabled
      requestData.attachmentPassword = this.getAvailableAttachmentPassword(data)
      requestData.auditExportDataRest = {
        refModel: this.moduleName,
        refId: this.resourceId,
        startDate: this.startDate,
        endDate: this.endDate,
      }
      return downloadApi(this.moduleName, requestData)
    },
    toggleSearchRow() {
      this.showSearchRow = !this.showSearchRow
    },
  },
}
</script>
<style lang="less" scoped>
.close-icon {
  position: absolute;
  top: 6px;
  right: 0;
}
</style>
